import React, { ReactElement } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemWrapper from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import { Chip } from '../Chip';
import { OptionsDropdown, OptionsParams } from '../OptionsDropdown';
import { Typography, CustomTypographyVariant } from '../Typography';
export interface LinkedListItemProps {
  /**
   * title / header copy
   */
  title: string;
  /**
   * the variant to use to render the title with
   */
  titleVariant?: CustomTypographyVariant;
  /**
   * optional, when provided the title is rendered as a link which opens this href in a new tab
   */
  titleHref?: string;
  /**
   * the subtitle
   */
  subTitle?: string | ReactElement;
  /**
   * optional, defaults to false (subtitle above title). When true, renders the subtitle below the title instead
   */
  subTitleBelowTitle?: boolean;
  /**
   * the icon to show in the card
   */
  icon?: ReactElement;
  /**
   * whether we are in mobile breakpoint
   */
  isSmallDevice: boolean;
  /**
   * optional, note: this only renders if isSupplier is falsy, it defines the list of options to
   * show when clicking on the ellipsis button
   */
  optionsParams?: OptionsParams;
  /**
   * optional, the status text to show next to the title
   */
  status?: string;
  /**
   * optional, the status chip text to show next to the title
   */
  tagText?: string;
  /**
   * optional, whether to enable/disable the ellipsis button
   */
  actionsEnabled?: boolean;
  /**
   * optional, renders a divider below the item - use for every row except the last in a group
   */
  divider?: boolean;
  /**
   * optional, defaults to false. When true, renders a skeleton placeholder row instead of the real
   * icon/title/subtitle - use while a row's real data hasn't arrived yet (e.g. a newly-linked item still
   * being fetched). `title`/`icon`/`subTitle` are ignored while loading.
   */
  isLoading?: boolean;
  /**
   * child component(s) which will be rendered as the last item in the ListItemWrapper
   */
  children?: JSX.Element | JSX.Element[];
}
/**
 * Renders a generic list component
 */
export const LinkedListItem: React.FC<React.PropsWithChildren<LinkedListItemProps>> = ({
  title,
  titleVariant,
  titleHref,
  subTitle,
  subTitleBelowTitle = false,
  icon,
  isSmallDevice,
  optionsParams,
  status,
  tagText,
  actionsEnabled = true,
  divider = false,
  isLoading = false,
  children,
}: LinkedListItemProps) => {
  if (isLoading) {
    return (
      <Box>
        <ListItemWrapper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            alignItems: 'center',
            padding: 0,
          }}
          data-testid="list-item"
        >
          <ListItemIcon>
            <Skeleton variant="circular" width={40} height={40} data-testid="list-item-icon-skeleton" />
          </ListItemIcon>
          <ListItemText>
            <Skeleton animation="wave" variant="text" width={160} data-testid="list-item-title-skeleton" />
          </ListItemText>
        </ListItemWrapper>
        {divider && <Divider data-testid="list-item-divider" sx={{ mt: 4 }} />}
      </Box>
    );
  }
  const isOptionsDropdownVisible = actionsEnabled && optionsParams?.options && optionsParams?.options?.length >= 1;
  const titleRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
      }}
    >
      {titleHref ? (
        <Box
          component="a"
          href={titleHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          <Typography variant={titleVariant}>{title}</Typography>
        </Box>
      ) : (
        <Typography variant={titleVariant}>{title}</Typography>
      )}
      {tagText ? <Chip variant="outlined" label={tagText} data-testid="list-item-tag" /> : ''}
      {Boolean(status) && (
        <Typography
          variant="body2"
          sx={{
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: '0.25rem',
            padding: 1,
          }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
  return (
    <Box>
      <ListItemWrapper
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          alignItems: 'center',
          padding: 0,
          position: 'relative',
        }}
        data-testid="list-item"
      >
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        <ListItemText>
          {subTitleBelowTitle ? (
            <>
              {titleRow}
              {subTitle ? subTitle : null}
            </>
          ) : (
            <>
              {subTitle ? subTitle : null}
              {titleRow}
            </>
          )}
        </ListItemText>
        {isOptionsDropdownVisible ? (
          <OptionsDropdown isSmallDevice={isSmallDevice} optionsParams={optionsParams} />
        ) : null}
        {children}
      </ListItemWrapper>
      {divider && <Divider data-testid="list-item-divider" sx={{ mt: 4 }} />}
    </Box>
  );
};
